import {
  IAddCreditInputModel,
  IOrderedWithPaginationInputModel,
  IPaymentIntentInputModel,
} from 'inputModels';
import {
  FinancialHistoryDataViewModel,
  IFinancialCardsDataViewModel,
  IPaymentIntentViewModel,
  IResponse,
  ITransactionViewModel,
} from 'viewModels';
import api from 'services/api';

const createPaymentIntent = (input: IPaymentIntentInputModel) =>
  new Promise<IResponse<IPaymentIntentViewModel>>((resolve, reject) => {
    api
      .post('payments/create-payment-intent', input)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const addCredit = (input: IAddCreditInputModel) =>
  new Promise<IResponse<ITransactionViewModel>>((resolve, reject) => {
    api
      .post('payments/add-credit', input)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getUserBalance = () =>
  new Promise<IResponse<number>>((resolve, reject) => {
    api
      .get('payments/get-user-balance')
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getAllTransactions = <T>(input?: IOrderedWithPaginationInputModel<T>) =>
  new Promise<IResponse<ITransactionViewModel[]>>((resolve, reject) => {
    api
      .get(
        `payments/get-user-transactions?page=${input?.page}&take=${
          input?.take
        }${input?.term ? `&term=${input?.term}` : ''}${
          input?.filter ? `&filter=${input?.filter}` : ''
        }&${input?.orderBy
          ?.map(
            (o, i) =>
              `orderBy[${i}].key=${o.key}&orderBy[${i}].direction=${o.direction}`
          )
          .join('&')}`
      )
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getUserTransactions = <T>(
  companyUserId?: string,
  input?: IOrderedWithPaginationInputModel<T>
) =>
  new Promise<IResponse<ITransactionViewModel[]>>((resolve, reject) => {
    const url = `payments/get-user-transactions?${
      companyUserId ? `companyUserId=${companyUserId}&` : ''
    }page=${input?.page || 1}&take=${input?.take || 10}${
      input?.term ? `&term=${input?.term}` : ''
    }${input?.filter ? `&filter=${input?.filter}` : ''}&${input?.orderBy
      ?.map(
        (o, i) =>
          `orderBy[${i}].key=${o.key}&orderBy[${i}].direction=${o.direction}`
      )
      .join('&')}`;

    api
      .get(url)
      .then(({ data }) => {
        resolve(data);
      })
      .catch(error => {
        console.error('Erro na API getUserTransactions:', error);
        reject(error);
      });
  });

const getUserTransactionsByPeriod = (
  companyUserId?: string,
  startDate?: string,
  endDate?: string
) =>
  new Promise<IResponse<ITransactionViewModel[]>>((resolve, reject) => {
    const queryParams = [
      companyUserId ? `companyUserId=${companyUserId}` : '',
      startDate ? `startDate=${encodeURIComponent(startDate)}` : '',
      endDate ? `endDate=${encodeURIComponent(endDate)}` : '',
    ]
      .filter(Boolean)
      .join('&');

    api
      .get(`payments/get-user-transactions-by-period?${queryParams}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getDashboardCardsData = (companyUserId?: string) =>
  new Promise<IResponse<IFinancialCardsDataViewModel>>((resolve, reject) => {
    api
      .get('payments/get-financial-cards-data', {
        params: { companyUserId },
      })
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const getFinancialHistoryData = (companyUserId?: string) =>
  new Promise<IResponse<FinancialHistoryDataViewModel[]>>((resolve, reject) => {
    api
      .get('payments/get-financial-history-data', {
        params: { companyUserId },
      })
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const download = (data: any, name: string) => {
  const blob = new Blob([data], {
    type: 'application/pdf',
  });
  const uri = window.URL.createObjectURL(blob);

  if (!uri) return;
  const link = document.createElement('a');

  link.href = uri;
  link.setAttribute('download', name);
  link.rel = 'noopener noreferrer';
  link.referrerPolicy = 'no-referrer';
  link.click();

  link.parentElement?.removeChild(link);
  window.URL.revokeObjectURL(uri);
};

export const exportInvoice = (
  companyUserId: string,
  detailed: boolean,
  startDate: string,
  endDate: string
): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    api
      .get('/payments/export-invoice', {
        params: {
          id: companyUserId,
          detailed,
          startDate,
          endDate,
        },
        responseType: 'blob',
      })
      .then(({ data }) => {
        const reportName = 'Fatura';
        download(data, `${reportName}.pdf`);
        resolve();
      })
      .catch(reject);
  });
};

export const PaymentsController = {
  addCredit,
  createPaymentIntent,
  getUserBalance,
  getAllTransactions,
  getUserTransactions,
  getDashboardCardsData,
  getFinancialHistoryData,
  exportInvoice,
  getUserTransactionsByPeriod,
};
